<template>
    <PlanbordGridRow>
        <PlanbordGridLeftDataColumns>
            <PlanbordGridCell info>
                <div class="budget-label">
                    <span>{{ label }}</span>
                    <span class="period-total">{{ getTotal() }}</span>
                </div>
            </PlanbordGridCell>
            <PlanbordFunctionCell />
            <PlanbordCommentCell />
            <PlanbordStampStartDateCell />
        </PlanbordGridLeftDataColumns>
        <template v-for="day in roster.days" :key="day.date.getTime()">
            <PlanbordGridCell class="budget-cell requirement" :holiday="day.holiday" :weekend="day.weekend" :class="getDayClasses(day)">
                {{ getHoursAmountCellContent(day) }}
            </PlanbordGridCell>
        </template>
    </PlanbordGridRow>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {BudgetCount, RosterDay, RosterData, BudgetDayCounters} from '@/types/types';
    import Dates from '@cquential/date-utils';
    import {Budget} from '@cquential/api';
    import PlanbordGridRow from '@/components/Planbord/PlanbordGridRow.vue';
    import PlanbordGridLeftDataColumns from '@/components/Planbord/PlanbordGridLeftDataColumns.vue';
    import PlanbordFunctionCell from '@/components/Planbord/PlanbordFunctionCell.vue';
    import PlanbordCommentCell from '@/components/Planbord/PlanbordCommentCell.vue';
    import PlanbordGridCell from '@/components/Planbord/PlanbordGridCell.vue';
    import PlanbordStampStartDateCell from '@/components/Planbord/PlanbordStampStartDateCell.vue';

    @Component({
        components: {
            PlanbordGridRow,
            PlanbordGridLeftDataColumns,
            PlanbordFunctionCell,
            PlanbordCommentCell,
            PlanbordGridCell,
            PlanbordStampStartDateCell,
        },
    })
    export default class BudgetedHoursRow extends Vue {
        @Prop({required: true}) public budget!: Budget;
        @Prop({required: true}) public roster!: RosterData;
        @Prop({required: true}) public budgetDayCounters!: BudgetDayCounters;
        @Prop({required: true}) public type!: 'used' | 'amount' | 'sum';

        public get label(): string {
            switch (this.type) {
                case 'used':
                    return 'Ingezet';
                case 'amount':
                    return 'Begroot';
                case 'sum':
                    return 'Verschil';
            }
        }

        public getDayClasses(day: RosterDay): string[] {
            const cssClass: string[] = [];

            const amount: number | null = this.getHoursAmount(day);
            if (amount !== null && amount < 0) {
                cssClass.push('negative');
            }

            if (this.type === 'sum') {
                cssClass.push('total');
            }

            return cssClass;
        }

        public getHoursAmountCellContent(day: RosterDay): string {
            const amount: number | null = this.getHoursAmount(day);
            if (amount === null) {
                return '';
            }

            return amount.toLocaleString(undefined, {maximumFractionDigits: 2});
        }

        public getHoursAmount(day: RosterDay): number | null {
            if (this.budget.id && this.budgetDayCounters && this.budgetDayCounters[this.budget.id]) {
                const formattedDate: string = Dates.Formatter.formatDateComputerFriendly(day.date);

                const budgetCount: BudgetCount | null = this.budgetDayCounters[this.budget.id][formattedDate] ?? null;
                if (budgetCount) {
                    return this.amount(budgetCount);
                }
            }

            return null;
        }

        public getTotal(): string {
            let total: number = 0;
            for (const day of this.roster.days) {
                total += this.getHoursAmount(day) ?? 0;
            }

            return total.toLocaleString(undefined, {maximumFractionDigits: 2});
        }

        public amount(budgetCount: BudgetCount): number | null {
            switch (this.type) {
                case 'amount':
                    return budgetCount.budgetAmount;
                case 'sum':
                    return (budgetCount.budgetAmount ?? 0) - (budgetCount.usedAmount ?? 0);
                case 'used':
                    return budgetCount.usedAmount;
            }
        };
    }
</script>

<style lang="scss">
    .budget-label {
        display: flex;
        justify-content: space-between;
        border-bottom: solid 0;

        span.period-total {
            padding: 1px 4px;
            margin: 0;
            margin-top: -3px;
            margin-bottom: -3px;
            width: 60px;
            text-align: center;
            font-weight: bold;
            background: var(--deployment-marker-default-background);
            border: solid 1px var(--deployment-marker-border-top-color);
        }
    }

    .budget-cell {
        padding: .4rem;
        text-align: center;

        &.total {
            border-top: solid 2px black;
        }

        &.negative {
            font-weight: bold;
            color: var(--negative-value-color);
        }
    }
</style>
